/** Local opt-in measurements. Never records customer text, identifiers or requests. */
export function installWorkbenchPerformanceTiming() {
  const pending = new Set<number>();
  const recordFrame = (
    operation: 'customer-name.input-to-frame' | 'quote.tab.input-to-frame',
  ) => {
    const start = performance.now();
    // The second frame bounds the first rendering opportunity after the input.
    // This is input-to-frame timing, not physical display latency or business readiness.
    const first = requestAnimationFrame(() => {
      pending.delete(first);
      const second = requestAnimationFrame(() => {
        pending.delete(second);
        const duration = performance.now() - start;
        performance.measure(`cpq.${operation}`, {
          start,
          end: performance.now(),
        });
        console.info(
          '[cpq-performance]',
          JSON.stringify({
            operation,
            durationMs: Math.round(duration * 100) / 100,
          }),
        );
      });
      pending.add(second);
    });
    pending.add(first);
  };
  const onInput = (event: Event) => {
    const element = event.target;
    if (
      element instanceof HTMLInputElement &&
      element.getAttribute('aria-label') === '客户名称'
    )
      recordFrame('customer-name.input-to-frame');
  };
  const onClick = (event: Event) => {
    const element = event.target;
    if (!(element instanceof Element)) return;
    const tab = element.closest('[role="tab"]');
    if (tab?.parentElement?.closest('[aria-label="动态报价标签"]'))
      recordFrame('quote.tab.input-to-frame');
  };
  document.addEventListener('input', onInput, true);
  document.addEventListener('click', onClick, true);
  return () => {
    document.removeEventListener('input', onInput, true);
    document.removeEventListener('click', onClick, true);
    for (const frame of pending) cancelAnimationFrame(frame);
    pending.clear();
  };
}

/** Fixed labels only; shared opt-in switch with HTTP/frame timing. */
export function startQuoteExportTiming(
  operation:
    | 'quote.export.image'
    | 'quote.export.xlsx'
    | 'quote.export.download'
    | 'quote.image.prepare'
    | 'quote.image.canvas'
    | 'quote.image.media'
    | 'quote.image.verify',
) {
  const enabled = import.meta.env.VITE_CPQ_PERFORMANCE_ENABLED === 'true';
  const start = enabled ? performance.now() : 0;
  let phaseStart = start;
  let phase = 'prepare';
  let ended = false;
  let poll: { attempt: number; status: string } | undefined;
  const record = (
    label: string,
    began: number,
    outcome?: 'success' | 'pending' | 'failed',
  ) => {
    if (!enabled) return;
    const end = performance.now();
    try {
      performance.measure(`cpq.${operation}.${label}`, { start: began, end });
      console.info(
        '[cpq-performance]',
        JSON.stringify({
          operation,
          phase: label,
          durationMs: Math.round((end - began) * 100) / 100,
          ...(outcome ? { outcome } : {}),
          ...(['poll-request', 'job-resume'].includes(label) && poll
            ? poll
            : {}),
        }),
      );
    } catch {
      // Diagnostics must never change export behavior.
    }
  };
  return {
    mediaUnavailable(count: number) {
      if (!enabled || ended || !Number.isInteger(count) || count < 0) return;
      console.info(
        '[cpq-performance]',
        JSON.stringify({ operation, phase: 'media-unavailable', count }),
      );
    },
    pollResult(attempt: number, status: string) {
      if (!enabled || ended || !['poll-request', 'job-resume'].includes(phase))
        return;
      if (
        !Number.isInteger(attempt) ||
        attempt < (phase === 'job-resume' ? 0 : 1) ||
        attempt > 80
      )
        return;
      if (
        !['QUEUED', 'RUNNING', 'SUCCEEDED', 'FAILED', 'CANCELED'].includes(
          status,
        )
      )
        return;
      poll = { attempt, status };
    },
    step(
      next:
        | 'media-load'
        | 'media-read'
        | 'image-prepare'
        | 'canvas-render'
        | 'snapshot'
        | 'sources'
        | 'job'
        | 'job-create'
        | 'job-resume'
        | 'poll-wait'
        | 'poll-request'
        | 'download'
        | 'download-headers'
        | 'download-body'
        | 'download-dispatch'
        | 'consume'
        | 'png'
        | 'image-decode'
        | 'image-draw'
        | 'png-encode',
    ) {
      if (ended) return;
      record(phase, phaseStart);
      poll = undefined;
      phase = next;
      phaseStart = enabled ? performance.now() : 0;
    },
    finish(outcome: 'success' | 'pending' | 'failed') {
      if (ended) return;
      ended = true;
      record(phase, phaseStart);
      record('total', start, outcome);
    },
  };
}
